/* @flow */

import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TranslatorProps } from 'react-i18next';

import { isIE } from 'utils/browserDetection';
import DashboardStore from 'stores/DashboardStore';
import { updateEditOwnedAsset, updateEditRentedAsset } from 'permissions/asset';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import type { CommonWidgetProps } from '../../commonTypes';
import ChartTable from '../../components/ChartTable/ChartTable';
import NivoPieChart from '../../components/NivoPieChart';
import NavigationService from 'services/NavigationService';

const STATIC_DATA = [
  {
    good: 5,
    excellent: 1,
    average: 4,
    poor: 0,
    scrap: 0,
    er_hire_end_date: null,
  },
];

export type AssetQualityProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class AssetQuality extends ComponentWithInjectedProps<
  AssetQualityProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetQuality.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.qualitystates ? dashboardStore.qualitystates['assets-quality'] : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadAssetsByQuality(tradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, size, widgetItem, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;
    const legendOrder = ['Excellent', 'Good', 'Average', 'Poor', 'Scrap'];
    const buttonLink = NavigationService.formulateReportViewUri('/assets', {
      page: 1,
      filters: [...(accountId ? [{ key: 'account', values: [accountId] }] : [])],
    });

    const pieColors = [
      'hsl(198, 93%, 38%)',
      'hsl(187, 84%, 56%)',
      'hsl(45, 82%, 65%)',
      'hsl(11, 89%, 60%)',
      'hsl(10, 73%, 45%)',
    ];

    const assetsQualityData = this.widgetData
      ? legendOrder
          .map((qualityStatus, index) => {
            const labelKey = qualityStatus.toLowerCase();

            return {
              id: qualityStatus,
              label: t(`general:${labelKey}`),
              value: this.widgetData[0][qualityStatus.toLowerCase()],
              color: index > 9 ? pieColors[index - 9] : pieColors[index],
            };
          })
          .filter(item => item.value > 0)
      : null;

    return (
      <NivoPieChart
        tradingAccount={dashboardStore.loadedTradingAccount}
        widgetInfos={[{ title: t('general:assets.owned_assets.quality'), variant: 'default' }]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        isLoading={this.shouldLoadData && dashboardStore.loadingAssetQuality}
        data={assetsQualityData}
        permissions={[...updateEditRentedAsset, ...updateEditOwnedAsset]}
        button={true}
        buttonLink={buttonLink}
        buttonText={t('general:actions.update_asset_plural')}
        totalKey="value"
        totalTitle={count => t('general:asset', { count })}
        renderLegend={(assetType: any) => {
          return (
            /* eslint-disable max-len */
            <Link
              key={assetType.id}
              to={NavigationService.formulateReportViewUri('/assets', {
                page: 1,
                filters: [
                  ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                  { key: 'asset_type', values: ['Owned'] },
                  { key: 'current_condition', values: [assetType.id] },
                ],
              })}
            >
              <ChartTable
                title={assetType.label}
                number={assetType.value}
                color={assetType.color}
              />
            </Link>
            /* eslint-enable max-len */
          );
        }}
        onClick={e => {
          const link = NavigationService.formulateReportViewUri('/assets', {
            page: 1,
            filters: [
              { key: 'asset_type', values: ['Owned'] },
              { key: 'current_condition', values: [e.id] },
              ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
            ],
          });

          NavigationService.navigateTo(link);
        }}
      />
    );
  }
}
